import React, { useState } from 'react';

export default function BarChart({ data, label, formatIncome }) {
  // data: [{ label, grams, income }]
  const [hovered, setHovered] = useState(null);

  if (!data || data.length === 0) return null;

  const W = 600, H = 200;
  const padL = 48, padR = 48, padT = 16, padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const maxGrams = Math.max(...data.map(d => d.grams), 1);
  const allIncome = data.map(d => d.income);
  const minIncome = Math.min(0, ...allIncome);
  const maxIncome = Math.max(...allIncome, 1);
  const incomeRange = maxIncome - minIncome || 1;

  const barW = (plotW / data.length) * 0.5;
  const barX = (i) => padL + (i + 0.5) * (plotW / data.length) - barW / 2;

  // Left axis (grams) grid
  const gramsGridSteps = 4;
  const gramsGrids = Array.from({ length: gramsGridSteps + 1 }, (_, i) => {
    const v = (maxGrams / gramsGridSteps) * i;
    const y = padT + plotH - (v / maxGrams) * plotH;
    return { v, y };
  });

  // Right axis (income) — map to same plot height
  const toIncomeY = v => padT + plotH - ((v - minIncome) / incomeRange) * plotH;
  const incomeGridSteps = 4;
  const incomeGrids = Array.from({ length: incomeGridSteps + 1 }, (_, i) => {
    const v = minIncome + (incomeRange / incomeGridSteps) * i;
    return { v, y: toIncomeY(v) };
  });

  // Income line points (centre of each bar group)
  const incomePoints = data.map((d, i) =>
    `${(barX(i) + barW / 2).toFixed(1)},${toIncomeY(d.income).toFixed(1)}`
  ).join(' ');

  const fmt = formatIncome || (v => `£${Math.abs(v).toFixed(0)}${v < 0 ? '-' : ''}`);

  return (
    <div>
      {label && <h4 style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</h4>}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
        {/* Grams grid lines + left labels */}
        {gramsGrids.map((g, i) => (
          <g key={i}>
            <line x1={padL} y1={g.y} x2={W - padR} y2={g.y}
              stroke="var(--border)" strokeWidth="0.5" />
            <text x={padL - 4} y={g.y + 4} textAnchor="end"
              fill="#63b3ed" fontSize="8">
              {g.v.toFixed(0)}g
            </text>
          </g>
        ))}

        {/* Right axis income labels */}
        {incomeGrids.map((g, i) => (
          <text key={i} x={W - padR + 4} y={g.y + 4} textAnchor="start"
            fill="#48bb78" fontSize="8">
            {fmt(g.v)}
          </text>
        ))}

        {/* Zero income line */}
        {minIncome < 0 && (
          <line x1={padL} y1={toIncomeY(0)} x2={W - padR} y2={toIncomeY(0)}
            stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="3 2" />
        )}

        {/* Bars (grams sold) */}
        {data.map((d, i) => {
          const bh = (d.grams / maxGrams) * plotH;
          const by = padT + plotH - bh;
          const isHovered = hovered === i;
          return (
            <rect key={i}
              x={barX(i)} y={by} width={barW} height={bh}
              rx="2"
              fill="#63b3ed"
              opacity={hovered !== null && !isHovered ? 0.35 : 0.85}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}

        {/* Income line */}
        {data.length > 1 && (
          <polyline points={incomePoints}
            fill="none" stroke="#48bb78" strokeWidth="2"
            strokeLinejoin="round" strokeLinecap="round" />
        )}

        {/* Income dots */}
        {data.map((d, i) => (
          <circle key={i}
            cx={barX(i) + barW / 2} cy={toIncomeY(d.income)}
            r={hovered === i ? 4 : 3}
            fill={d.income >= 0 ? '#48bb78' : '#fc8181'}
            stroke="var(--bg-primary)" strokeWidth="1.5"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}

        {/* X labels */}
        {data.map((d, i) => (
          <text key={i}
            x={barX(i) + barW / 2} y={H - padB + 14}
            textAnchor="middle" fill="var(--text-muted)" fontSize="8">
            {d.label.length > 10 ? d.label.slice(0, 10) + '…' : d.label}
          </text>
        ))}

        {/* Hover tooltip */}
        {hovered !== null && (() => {
          const d = data[hovered];
          const cx = barX(hovered) + barW / 2;
          const tipW = 110, tipH = 46;
          const tipX = Math.min(Math.max(cx - tipW / 2, padL), W - padR - tipW);
          const tipY = padT + 4;
          return (
            <g style={{ pointerEvents: 'none' }}>
              <rect x={tipX} y={tipY} width={tipW} height={tipH}
                rx="4" fill="var(--bg-secondary)" stroke="var(--border)" strokeWidth="1" />
              <text x={tipX + 8} y={tipY + 13} fill="var(--text-secondary)" fontSize="9" fontWeight="600">
                {d.label}
              </text>
              <rect x={tipX + 8} y={tipY + 19} width={6} height={6} rx="1" fill="#63b3ed" />
              <text x={tipX + 18} y={tipY + 26} fill="var(--text-secondary)" fontSize="9">
                {d.grams.toFixed(1)}g sold
              </text>
              <rect x={tipX + 8} y={tipY + 33} width={6} height={6} rx="1" fill={d.income >= 0 ? '#48bb78' : '#fc8181'} />
              <text x={tipX + 18} y={tipY + 40} fill="var(--text-secondary)" fontSize="9">
                {d.income >= 0 ? '' : '-'}{fmt(d.income)} net
              </text>
            </g>
          );
        })()}
      </svg>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, fontSize: 11, marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 12, height: 12, background: '#63b3ed', borderRadius: 2, opacity: 0.85 }} />
          <span style={{ color: 'var(--text-secondary)' }}>Grams Sold (left axis)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 18, height: 3, background: '#48bb78', borderRadius: 2 }} />
          <span style={{ color: 'var(--text-secondary)' }}>Net Income (right axis)</span>
        </div>
      </div>
    </div>
  );
}
